/**
 * PhotoBoothPage
 * Main kiosk experience - default landing page after login
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuestionnaireForm } from '@/components/QuestionnaireForm';
import { IOSInstallPrompt } from '@/components/IOSInstallPrompt';
import { getActiveQuestionnaire } from '@/services/questionnaire.service';
import { getCurrentUser } from '@/services/auth.service';
import { submitGuestSession } from '@/services/guest.service';
import { getOrganization } from '@/services/organization.service';
import { storage } from '@/services/firebase';
import { ref, getBlob } from 'firebase/storage';
import { Questionnaire } from '@/types';
import './styles/PhotoBoothPage.css';

// Random session id; crypto.randomUUID needs iPadOS 15.4+, so keep a fallback
function newSubmissionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

type PhotoBoothState =
  | 'idle'
  | 'camera'
  | 'filter-select'
  | 'review'
  | 'questionnaire';
type FilterType = 'none' | 'vintage' | 'date' | 'logo';

export function PhotoBoothPage() {
  const navigate = useNavigate();

  const [state, setState] = useState<PhotoBoothState>('idle');
  const [countdown, setCountdown] = useState<number | null>(null);
  const [photos, setPhotos] = useState<string[]>([]);
  const [isFlashing, setIsFlashing] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<FilterType>('none');
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(
    null
  );
  const [isLoadingQuestionnaire, setIsLoadingQuestionnaire] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [queuedOffline, setQueuedOffline] = useState(false);
  // The capture being submitted; sessionId stays stable across retries so a
  // re-submission overwrites the same session instead of duplicating it
  const lastSubmissionRef = useRef<{
    responses: Record<string, string | number | boolean | string[]>;
    sessionId: string;
  } | null>(null);
  const [orgLogoUrl, setOrgLogoUrl] = useState<string | null>(null);
  const [logoPosition, setLogoPosition] = useState<
    'top-left' | 'bottom-center'
  >('top-left');
  // Mirror preview + capture (selfie style). Org setting; defaults to on.
  // The ref is read inside capturePhoto, which runs from timers whose
  // closures may predate the async org load.
  const [mirrorPreview, setMirrorPreview] = useState(true);
  const mirrorPreviewRef = useRef(true);

  // Computed filters based on org logo availability
  const availableFilters = [
    { id: 'none' as FilterType, name: 'No Filter', description: 'Original' },
    {
      id: 'vintage' as FilterType,
      name: 'Vintage Camera',
      description: 'Classic film look',
    },
    {
      id: 'date' as FilterType,
      name: 'Date Stamp',
      description: 'Add date to photo',
    },
    ...(orgLogoUrl
      ? [
          {
            id: 'logo' as FilterType,
            name: 'Logo',
            description: 'Add your logo',
          },
        ]
      : []),
  ];

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const timeoutsRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const isCapturingRef = useRef(false);
  const logoImageRef = useRef<HTMLImageElement | null>(null);

  const clearTimers = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    for (const timeoutId of timeoutsRef.current) {
      clearTimeout(timeoutId);
    }
    timeoutsRef.current = [];
  };

  const stopCamera = () => {
    clearTimers();

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Load organization settings (logo, mirror preference) on mount
  useEffect(() => {
    const loadOrgSettings = async () => {
      const user = getCurrentUser();
      if (user) {
        const org = await getOrganization(user.uid);
        const mirror = org?.mirrorPreview !== false;
        mirrorPreviewRef.current = mirror;
        setMirrorPreview(mirror);
        if (org?.branding?.logoUrl) {
          setOrgLogoUrl(org.branding.logoUrl);

          // Preload the logo image for canvas rendering
          // Use Firebase Storage SDK to download blob directly (handles auth)
          try {
            const logoRef = ref(storage, `organizations/${user.uid}/logo`);
            const blob = await getBlob(logoRef);

            // Convert blob to data URL
            const reader = new FileReader();
            reader.onload = () => {
              const dataUrl = reader.result as string;
              const img = new Image();
              img.onload = () => {
                console.log(
                  'Logo preloaded successfully:',
                  img.width,
                  'x',
                  img.height
                );
                logoImageRef.current = img;
              };
              img.onerror = (e) => {
                console.error('Failed to load logo image:', e);
              };
              img.src = dataUrl;
            };
            reader.onerror = (e) => {
              console.error('Failed to read logo blob:', e);
            };
            reader.readAsDataURL(blob);
          } catch (error) {
            console.error('Failed to load logo:', error);
          }
        }
      }
    };
    void loadOrgSettings();
  }, []);

  const waitForVideoReady = async (video: HTMLVideoElement) => {
    if (video.videoWidth > 0 && video.videoHeight > 0) return;

    await new Promise<void>((resolve) => {
      const onLoaded = () => {
        video.removeEventListener('loadedmetadata', onLoaded);
        resolve();
      };

      video.addEventListener('loadedmetadata', onLoaded);

      // Safety timeout in case the event doesn't fire as expected.
      const timeoutId = setTimeout(() => {
        video.removeEventListener('loadedmetadata', onLoaded);
        resolve();
      }, 2000);
      timeoutsRef.current.push(timeoutId);
    });
  };

  const startCountdown = (seconds: number) => {
    // Only clear countdown + scheduled timeouts; camera stream stays alive.
    clearTimers();
    isCapturingRef.current = false;
    setCountdown(seconds);

    const intervalId = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null) return prev;

        if (prev <= 1) {
          clearInterval(intervalId);
          countdownIntervalRef.current = null;

          if (!isCapturingRef.current) {
            isCapturingRef.current = true;
            const captureTimeout = setTimeout(() => {
              capturePhoto();
              isCapturingRef.current = false;
            }, 100);
            timeoutsRef.current.push(captureTimeout);
          }

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    countdownIntervalRef.current = intervalId;
  };

  const startCamera = async () => {
    try {
      let stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
          aspectRatio: { ideal: 16 / 9 },
        },
        audio: false,
      });

      // Try to find a standard (non-wide) camera explicitly
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((d) => d.kind === 'videoinput');
        const currentTrack = stream.getVideoTracks()[0];
        const currentLabel = currentTrack.label.toLowerCase();

        // If the current camera is wide, try to find a regular front camera
        if (currentLabel.includes('wide') || currentLabel.includes('ultra')) {
          const standardFrontCamera = videoDevices.find(
            (d) =>
              (d.label.toLowerCase().includes('front') || d.label.toLowerCase().includes('user')) &&
              !d.label.toLowerCase().includes('wide') &&
              !d.label.toLowerCase().includes('ultra')
          );

          if (standardFrontCamera) {
            stream.getTracks().forEach((track) => track.stop());
            stream = await navigator.mediaDevices.getUserMedia({
              video: {
                deviceId: { exact: standardFrontCamera.deviceId },
                width: { ideal: 1920, min: 1280 },
                height: { ideal: 1080, min: 720 },
                aspectRatio: { ideal: 16 / 9 },
              },
              audio: false,
            });
          }
        }
      } catch (err) {
        console.warn('Error selecting specific camera lens:', err);
      }

      streamRef.current = stream;

      // Force normal (non-wide) zoom on iPads with ultra-wide front cameras.
      // The ultra-wide camera defaults to its widest FOV (zoom: 1).
      // Setting zoom to ~2 brings it to the standard un-warped framing.
      const track = stream.getVideoTracks()[0];
      if (track) {
        type ZoomCapabilities = MediaTrackCapabilities & {
          zoom?: { min: number; max: number };
        };
        type ZoomConstraintSet = MediaTrackConstraintSet & { zoom?: number };

        const capabilities = track.getCapabilities() as ZoomCapabilities;
        const trackLabel = track.label.toLowerCase();
        
        if (capabilities.zoom) {
          let targetZoom = capabilities.zoom.min;
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

          // If we are on iOS/iPadOS and trapped with a wide camera (or just 'front camera' on iPad),
          // aim for a zoom of 2.0 to crop out the wide-angle distortion.
          if (isIOS && (trackLabel.includes('wide') || trackLabel.includes('ultra') || trackLabel === 'front camera')) {
             targetZoom = 1.0; 
          }

          const normalZoom = Math.min(
            Math.max(capabilities.zoom.min || 1, targetZoom),
            capabilities.zoom.max || 1
          );

          await track
            .applyConstraints({
              advanced: [{ zoom: normalZoom } as ZoomConstraintSet],
            })
            .catch(() => {
              // Zoom not supported — ignore
            });
        }
      }

      const attach = async () => {
        const video = videoRef.current;
        if (!video) {
          const retryId = setTimeout(() => {
            void attach();
          }, 50);
          timeoutsRef.current.push(retryId);
          return;
        }

        video.srcObject = stream;

        await waitForVideoReady(video);
        await video.play().catch(() => {
          // ignore; device autoplay policies vary
        });

      };

      void attach();
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please check permissions.');
      stopCamera();
      setState('idle');
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;

    if (!video.videoWidth || !video.videoHeight) {
      // Video not ready yet; try again quickly.
      const retryTimeout = setTimeout(() => capturePhoto(), 100);
      timeoutsRef.current.push(retryTimeout);
      return;
    }

    // Flash effect
    setIsFlashing(true);
    const flashTimeout = setTimeout(() => setIsFlashing(false), 180);
    timeoutsRef.current.push(flashTimeout);

    // Capture current frame
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Match the capture to the preview: flip horizontally only when the org
    // has mirroring on. Save/restore so overlays below draw un-mirrored.
    context.save();
    if (mirrorPreviewRef.current) {
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
    }

    // Apply CSS filter to canvas context before drawing
    if (currentFilter === 'vintage') {
      context.filter =
        'sepia(40%) contrast(110%) brightness(90%) saturate(130%) hue-rotate(-5deg)';
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    context.restore();

    // Add date stamp if date filter is active
    if (currentFilter === 'date') {
      const dateText = new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });

      const fontSize = Math.floor(canvas.height / 15); // Responsive font size
      context.font = `700 ${fontSize}px Caveat, cursive`;
      context.textAlign = 'center';
      context.textBaseline = 'bottom';

      // Draw text shadow for readability
      context.fillStyle = 'rgba(0, 0, 0, 0.7)';
      context.fillText(dateText, canvas.width / 2 + 3, canvas.height - 120 + 3);

      // Draw white semi-transparent text
      context.fillStyle = 'rgba(255, 255, 255, 0.85)';
      context.fillText(dateText, canvas.width / 2, canvas.height - 120);
    }

    // Add logo if logo filter is active
    if (currentFilter === 'logo' && logoImageRef.current) {
      console.log('Drawing logo on canvas');
      const logoImg = logoImageRef.current;
      const maxLogoWidth = canvas.width * 0.17; // Logo max 20% of canvas width
      const maxLogoHeight = canvas.height * 0.17; // Logo max 20% of canvas height

      const scale = Math.min(
        maxLogoWidth / logoImg.width,
        maxLogoHeight / logoImg.height,
        1 // Don't upscale
      );

      const logoWidth = logoImg.width * scale;
      const logoHeight = logoImg.height * scale;
      const padding = 30;

      // Position logo based on logoPosition state
      let logoX, logoY;
      if (logoPosition === 'top-left') {
        logoX = padding;
        logoY = padding;
      } else {
        // bottom-center: center horizontally, position above bottom
        logoX = (canvas.width - logoWidth) / 2;
        logoY = canvas.height - logoHeight - 200; // 200px from bottom to clear button area
      }

      context.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
      console.log(
        'Logo drawn at',
        logoX,
        logoY,
        'size:',
        logoWidth,
        'x',
        logoHeight
      );
    } else if (currentFilter === 'logo') {
      console.log(
        'Logo filter active but image not loaded:',
        logoImageRef.current
      );
    }

    const photoDataUrl = canvas.toDataURL('image/jpeg', 1.0);
    // const photoDataUrl = canvas.toDataURL('image/png'); //lossless for larger size and better quality

    setCountdown(null);

    setPhotos((prevPhotos) => {
      const nextPhotos = [...prevPhotos, photoDataUrl];
      const count = nextPhotos.length;

      if (count < 3) {
        const nextCountdownTimeout = setTimeout(() => startCountdown(3), 700);
        timeoutsRef.current.push(nextCountdownTimeout);
      } else {
        const finishTimeout = setTimeout(() => {
          stopCamera();
          setState('review');
        }, 450);
        timeoutsRef.current.push(finishTimeout);
      }

      return nextPhotos.slice(0, 3);
    });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart) return;

    // Check if touch originated from button or filter selection area
    const target = e.target as HTMLElement;
    const isButton =
      target.closest('.start-countdown-button') ||
      target.closest('.filter-select-bottom');

    // If touchEnd wasn't set (no movement), treat it as a tap at the start position
    const endX = touchEnd ?? touchStart;

    const distance = touchStart - endX;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    const isTap = Math.abs(distance) < 10; // Minimal movement = tap

    // If logo filter is active and it's a tap (not a swipe) AND not on a button, toggle position
    if (currentFilter === 'logo' && isTap && !isButton) {
      setLogoPosition((prev) =>
        prev === 'top-left' ? 'bottom-center' : 'top-left'
      );
      return; // Don't process as swipe
    }

    if (isLeftSwipe) {
      // Next filter
      const currentIndex = availableFilters.findIndex(
        (f) => f.id === currentFilter
      );
      const nextIndex = (currentIndex + 1) % availableFilters.length;
      setCurrentFilter(availableFilters[nextIndex].id);
    }

    if (isRightSwipe) {
      // Previous filter
      const currentIndex = availableFilters.findIndex(
        (f) => f.id === currentFilter
      );
      const prevIndex =
        (currentIndex - 1 + availableFilters.length) % availableFilters.length;
      setCurrentFilter(availableFilters[prevIndex].id);
    }
  };

  const handleStartCountdown = () => {
    setState('camera');
    const startTimeout = setTimeout(() => startCountdown(5), 200);
    timeoutsRef.current.push(startTimeout);
  };

  const handleTakePhoto = () => {
    // Render filter selection view immediately so the video element exists.
    setPhotos([]);
    setCountdown(null);
    setIsFlashing(false);

    setState('filter-select');
    void startCamera();
  };

  const handleCancel = () => {
    stopCamera();
    setPhotos([]);
    setCountdown(null);
    setIsFlashing(false);
    setIsSubmitting(false);
    setCurrentFilter('none');
    setLogoPosition('top-left');
    setQuestionnaire(null);

    setState('idle');
  };

  const handleRetake = () => {
    stopCamera();
    setPhotos([]);
    setCountdown(null);
    setIsFlashing(false);
    setIsSubmitting(false);
    setCurrentFilter('none');
    setLogoPosition('top-left');

    setState('idle');
  };

  const handleNext = async () => {
    // Load active questionnaire
    setIsLoadingQuestionnaire(true);
    try {
      const user = getCurrentUser();
      if (!user) {
        alert('Please sign in to continue.');
        navigate('/login');
        return;
      }

      const activeQuestionnaire = await getActiveQuestionnaire(user.uid);

      if (!activeQuestionnaire) {
        console.error('No active questionnaire found');
        alert('No questionnaire is currently active. Please contact support.');
        return;
      }

      setQuestionnaire(activeQuestionnaire);
      setState('questionnaire');
    } catch (error) {
      console.error('Error loading questionnaire:', error);
      alert('Unable to load questionnaire. Please try again.');
    } finally {
      setIsLoadingQuestionnaire(false);
    }
  };

  const handleQuestionnaireSubmit = async (
    responses: Record<string, string | number | boolean | string[]>
  ) => {
    if (isSubmitting || !questionnaire) return;

    // Get current user (orgId)
    const user = getCurrentUser();
    if (!user) {
      navigate('/login');
      return;
    }

    // Extract email from responses (always included as email question)
    const email = responses.email as string;
    if (!email) {
      setSubmitError(
        'We couldn’t find an email in your answers, so we have nowhere to send your photos. Please start over and include your email.'
      );
      return;
    }

    // A retry replays the same responses object and must keep its sessionId;
    // a fresh capture gets a new one.
    if (lastSubmissionRef.current?.responses !== responses) {
      lastSubmissionRef.current = { responses, sessionId: newSubmissionId() };
    }
    const { sessionId } = lastSubmissionRef.current;

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      // Wait for the real upload before celebrating — telling the guest
      // "photos sent" while the submission silently fails loses their photos.
      const result = await submitGuestSession(
        email,
        user.uid,
        questionnaire.id,
        responses,
        photos,
        sessionId
      );

      // Offline submissions are queued and synced when connection returns
      setQueuedOffline(result.queued);
      setShowConfetti(true);

      // Reset after 5 seconds
      setTimeout(() => {
        setShowConfetti(false);
        handleRetake();
      }, 5000);
    } catch (error) {
      console.error('Error submitting session:', error);
      setSubmitError(
        'Your photos are still here — check the wifi and give it another try.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetrySubmit = () => {
    if (lastSubmissionRef.current) {
      handleQuestionnaireSubmit(lastSubmissionRef.current.responses);
    }
  };

  const handleQuestionnaireBack = () => {
    setState('review');
  };

  return (
    <div className="photobooth-page">
      {/* iOS Install Prompt */}
      <IOSInstallPrompt />

      {/* Logo */}
      {state !== 'camera' && state !== 'filter-select' && (
        <div className="logo-container">
          <img
            src="/assets/images/logo-long-white.png"
            alt="Sunday Best"
            className="photobooth-logo"
          />
        </div>
      )}

      {/* Dashboard Button */}
      {state !== 'camera' && state !== 'filter-select' && (
        <button
          onClick={() => navigate('/admin')}
          className="dashboard-button"
          aria-label="Admin Dashboard"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="7" height="7" rx="2"></rect>
            <rect x="14" y="3" width="7" height="7" rx="2"></rect>
            <rect x="14" y="14" width="7" height="7" rx="2"></rect>
            <rect x="3" y="14" width="7" height="7" rx="2"></rect>
          </svg>
        </button>
      )}

      {/* Cancel Button */}
      {state !== 'idle' && (
        <button
          onClick={handleCancel}
          className="cancel-button"
          aria-label="Cancel and return to start"
        >
          ✕
        </button>
      )}

      {/* Main Content */}
      {state === 'idle' && (
        <div className="photobooth-content">
          <div className="photobooth-message">
            <h2 className="message-main">Wow. You look good!</h2>
            <p className="message-sub">Tap to start</p>
          </div>
          <button onClick={handleTakePhoto} className="take-photo-button">
            <img
              src="/assets/images/logo-camera-blue.png"
              alt="Take Photo"
              width="100"
            />
          </button>
        </div>
      )}

      {(state === 'filter-select' || state === 'camera') && (
        <div
          className={
            state === 'filter-select'
              ? 'filter-select-container'
              : 'camera-container'
          }
          onTouchStart={
            state === 'filter-select' ? handleTouchStart : undefined
          }
          onTouchMove={state === 'filter-select' ? handleTouchMove : undefined}
          onTouchEnd={state === 'filter-select' ? handleTouchEnd : undefined}
          aria-label={state === 'camera' ? 'Camera preview' : undefined}
          style={{ touchAction: 'none' }}
        >
          <video
            ref={videoRef}
            className={`camera-view ${currentFilter !== 'none' ? `filter-${currentFilter}` : ''}`}
            style={{ transform: mirrorPreview ? 'scaleX(-1)' : 'none' }}
            autoPlay
            playsInline
            muted
          />
          <canvas ref={canvasRef} className="camera-canvas" />

          {/* Date overlay for live preview */}
          {currentFilter === 'date' && (
            <div className="date-overlay">
              {new Date().toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
          )}

          {/* Logo overlay for live preview */}
          {currentFilter === 'logo' && orgLogoUrl && (
            <div className={`logo-overlay logo-overlay-${logoPosition}`}>
              <img src={orgLogoUrl} alt="Organization Logo" />
            </div>
          )}

          {/* Tap to change instruction for logo filter */}
          {currentFilter === 'logo' && state === 'filter-select' && (
            <div className="tap-to-change-text">Tap to change</div>
          )}

          {state === 'filter-select' && (
            <>
              {/* Start button */}
              <div className="filter-select-bottom">
                <div className="filter-dots">
                  {availableFilters.map((filter) => (
                    <div
                      key={filter.id}
                      className={`filter-dot ${
                        currentFilter === filter.id ? 'active' : ''
                      }`}
                    ></div>
                  ))}
                </div>
                <button
                  onClick={handleStartCountdown}
                  className="start-countdown-button"
                >
                  Start Photobooth
                </button>
              </div>
            </>
          )}

          {state === 'camera' && (
            <>
              {countdown !== null && countdown > 0 && (
                <div className="countdown-overlay" aria-live="polite">
                  <div className="countdown-number">{countdown}</div>
                </div>
              )}

              {isFlashing && <div className="camera-flash" />}

              <div className="photo-progress">
                Photo {Math.min(photos.length + 1, 3)} of 3
              </div>
            </>
          )}
        </div>
      )}

      {state === 'review' && (
        <div className="review-container">
          <div className="review-message">
            <h2 className="message-main">Looking great!</h2>
          </div>

          <div className="photos-grid">
            {photos.map((photo, index) => (
              <div key={index} className="photo-item">
                <img
                  src={photo}
                  alt={`Photo ${index + 1}`}
                  className="captured-photo"
                />
                <span className="photo-number">{index + 1}</span>
              </div>
            ))}
          </div>

          <div className="review-actions">
            <button onClick={handleRetake} className="btn-retake">
              Retake
            </button>
            <button
              onClick={handleNext}
              className="btn-next"
              disabled={isLoadingQuestionnaire}
            >
              {isLoadingQuestionnaire ? 'Loading...' : 'Next'}
            </button>
          </div>
        </div>
      )}

      {state === 'questionnaire' && questionnaire && (
        <div className="questionnaire-view">
          <QuestionnaireForm
            questions={questionnaire.questions}
            onSubmit={handleQuestionnaireSubmit}
            onBack={handleQuestionnaireBack}
          />
        </div>
      )}

      {/* Sending overlay — shown while photos upload */}
      {isSubmitting && !showConfetti && (
        <div className="submit-overlay">
          <div className="submit-card">
            <div className="submit-spinner" aria-label="Sending" />
            <h2>Sending your photos…</h2>
            <p>Hang tight, this only takes a moment.</p>
          </div>
        </div>
      )}

      {/* Submission failed — let the guest retry instead of losing photos */}
      {submitError && !isSubmitting && (
        <div className="submit-overlay">
          <div className="submit-card">
            <div className="submit-error-icon">😕</div>
            <h2>Hmm, that didn’t go through</h2>
            <p>{submitError}</p>
            <div className="submit-card-buttons">
              <button
                onClick={handleRetrySubmit}
                className="submit-retry-button"
              >
                Try Again
              </button>
              <button
                onClick={() => {
                  setSubmitError(null);
                  handleRetake();
                }}
                className="submit-cancel-button"
              >
                Start Over
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Confetti */}
      {showConfetti && (
        <div className="confetti-overlay">
          <div className="confetti-container">
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                className="confetti-piece"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  backgroundColor: [
                    '#FF6B6B',
                    '#4ECDC4',
                    '#45B7D1',
                    '#FFA07A',
                    '#98D8C8',
                    '#F7DC6F',
                  ][Math.floor(Math.random() * 6)],
                }}
              ></div>
            ))}
          </div>
          <div className="success-message">
            <div className="success-icon">🎉</div>
            <h1>All Done!</h1>
            <p className="success-text">
              {queuedOffline
                ? 'You’re offline right now — your photos will be sent automatically as soon as the connection is back.'
                : 'Your photos will be sent to your email shortly.'}
            </p>
            <p className="success-tag">
              Tag us at <strong>#sundaybestapp</strong> 📸
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
